import { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { Camera, X, Upload, MapPin, Sparkles, Heart } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Heart-shape matrix (15 cols x 13 rows). 1 = photo cell, 0 = empty.
// Classic pixel-heart that tapers to a tip at the bottom.
const HEART_MATRIX = [
  [0, 0, 1, 1, 1, 1, 0, 0, 0, 1, 1, 1, 1, 0, 0],
  [0, 1, 1, 1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
  [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
  [0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0],
  [0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
];

const NEW_WINDOW_MS = 24 * 60 * 60 * 1000; // 24h

function computeCellOrder() {
  // Center-out reveal: compute each "1" cell's distance from the geometric center,
  // then return ordered indices (closest first).
  const cols = HEART_MATRIX[0].length;
  const rows = HEART_MATRIX.length;
  const cx = (cols - 1) / 2;
  const cy = rows * 0.45; // heart "visual center" is slightly above middle
  const cells = [];
  let idx = 0;
  HEART_MATRIX.forEach((row, y) => {
    row.forEach((v, x) => {
      if (v === 1) {
        const dx = x - cx;
        const dy = y - cy;
        cells.push({ idx: idx++, dist: Math.sqrt(dx * dx + dy * dy) });
      }
    });
  });
  cells.sort((a, b) => a.dist - b.dist);
  // Map original index -> reveal order position
  const order = new Array(cells.length);
  cells.forEach((c, pos) => {
    order[c.idx] = pos;
  });
  return order;
}

export default function RebelTeamSection({ content }) {
  const { t, lang } = useLanguage();
  const [photos, setPhotos] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const sectionRef = useRef();

  const cellOrder = useMemo(computeCellOrder, []);
  const totalCells = cellOrder.length; // ~100

  const load = () => {
    axios.get(`${API}/fan-photos?approved_only=true&limit=120`)
      .then(res => setPhotos(res.data))
      .catch(console.error);
  };

  useEffect(load, []);

  // Progressive reveal on scroll into view
  useEffect(() => {
    if (!sectionRef.current || revealed) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setRevealed(true);
        obs.disconnect();
      }
    }, { threshold: 0.15 });
    obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, [revealed]);

  const title = (lang === "cz" ? content?.rebelteam_title_cz : content?.rebelteam_title_en) || t.rebelteam.title;
  const subtitle = (lang === "cz" ? content?.rebelteam_subtitle_cz : content?.rebelteam_subtitle_en) || t.rebelteam.subtitle;

  const resolve = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);
  const now = Date.now();

  return (
    <section
      ref={sectionRef}
      data-testid="rebelteam-section"
      style={{ background: "#050505", padding: "5rem 0 6rem", position: "relative", overflow: "hidden" }}
    >
      {/* Background accents */}
      <div style={{ position: "absolute", top: "30%", left: "-20%", width: "700px", height: "700px", background: "radial-gradient(circle, rgba(255,0,90,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "10%", right: "-15%", width: "500px", height: "500px", background: "radial-gradient(circle, rgba(0,229,255,0.05) 0%, transparent 70%)", pointerEvents: "none" }} />

      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="section-label mb-3" style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
            <Heart size={12} fill="#FF005A" style={{ color: "#FF005A" }} /> FANOUŠCI / FANS
          </p>
          <h2
            data-testid="rebelteam-title"
            style={{
              fontFamily: "Unbounded, sans-serif",
              fontWeight: 900,
              fontSize: "clamp(2.5rem, 7vw, 5rem)",
              color: "#FFFFFF",
              textTransform: "uppercase",
              letterSpacing: "-0.03em",
              background: "linear-gradient(135deg, #FF005A 0%, #FFFFFF 50%, #FF005A 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              marginBottom: "0.75rem",
            }}
          >
            {title}
          </h2>
          <p style={{ color: "#A1A1AA", fontSize: "1rem", maxWidth: "560px", margin: "0 auto 1.5rem", fontFamily: "Manrope, sans-serif" }}>
            {subtitle}
          </p>
          <button
            data-testid="share-photo-btn"
            onClick={() => setModalOpen(true)}
            className="btn-rebel rounded-none inline-flex items-center gap-2"
            style={{ fontSize: "0.7rem", padding: "0.85rem 1.8rem" }}
          >
            <Camera size={14} /> {t.rebelteam.shareBtn}
          </button>
        </div>

        {/* Heart collage */}
        <HeartCollage
          photos={photos}
          revealed={revealed}
          cellOrder={cellOrder}
          totalCells={totalCells}
          now={now}
          resolve={resolve}
          onClickPhoto={setLightbox}
        />

        {/* Counter */}
        <div className="text-center mt-10">
          <p data-testid="rebelteam-count" style={{ color: "#A1A1AA", fontSize: "0.85rem", fontFamily: "Manrope, sans-serif" }}>
            <span style={{ color: "#FF005A", fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "1.4rem" }}>
              {photos.length}
            </span>
            <span style={{ marginLeft: "0.5rem", letterSpacing: "0.1em", textTransform: "uppercase", fontSize: "0.7rem" }}>
              {lang === "cz" ? "fanouškovských fotek · sdílej svoji!" : "fan photos · share yours!"}
            </span>
          </p>
        </div>
      </div>

      {modalOpen && <UploadModal onClose={() => setModalOpen(false)} onSuccess={load} />}
      {lightbox && <Lightbox photo={lightbox} onClose={() => setLightbox(null)} resolve={resolve} />}
    </section>
  );
}

function HeartCollage({ photos, revealed, cellOrder, totalCells, now, resolve, onClickPhoto }) {
  const { t } = useLanguage();

  if (photos.length === 0) {
    return (
      <div
        data-testid="rebelteam-empty"
        style={{
          textAlign: "center",
          padding: "4rem 1.5rem",
          border: "1px dashed rgba(255,255,255,0.12)",
          borderRadius: "16px",
          maxWidth: "600px",
          margin: "0 auto",
        }}
      >
        <Camera size={40} color="#52525B" style={{ margin: "0 auto 1rem" }} />
        <p style={{ color: "#A1A1AA", fontFamily: "Manrope, sans-serif", fontSize: "0.9rem" }}>
          {t.rebelteam.noPhotos}
        </p>
      </div>
    );
  }

  let cellIdx = 0;
  return (
    <div
      data-testid="rebelteam-heart"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${HEART_MATRIX[0].length}, 1fr)`,
        gap: "3px",
        maxWidth: "780px",
        margin: "0 auto",
        aspectRatio: `${HEART_MATRIX[0].length} / ${HEART_MATRIX.length}`,
      }}
    >
      {HEART_MATRIX.flatMap((row, y) =>
        row.map((v, x) => {
          if (v === 0) {
            return <div key={`${y}-${x}`} aria-hidden style={{ visibility: "hidden" }} />;
          }
          const thisCellIdx = cellIdx++;
          const revealPos = cellOrder[thisCellIdx] ?? thisCellIdx;
          const photo = photos[thisCellIdx % photos.length];
          const isNew = photo?.created_at && (now - new Date(photo.created_at).getTime() < NEW_WINDOW_MS);
          const delay = (revealPos / totalCells) * 1.2; // 0..1.2s
          return (
            <div
              key={`${y}-${x}`}
              data-testid={`heart-cell-${thisCellIdx}`}
              onClick={() => onClickPhoto(photo)}
              className={`heart-cell ${revealed ? "is-visible" : ""} ${isNew ? "is-new" : ""}`}
              style={{
                animationDelay: `${delay}s`,
                background: "#0A0A0A",
              }}
              title={photo?.author_name}
            >
              <img
                src={resolve(photo.image_url)}
                alt={photo.author_name}
                loading="lazy"
                onError={(e) => { e.target.style.opacity = 0.15; }}
              />
              {isNew && (
                <span className="heart-cell__new-badge" aria-label="new">
                  NEW
                </span>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

function Lightbox({ photo, onClose, resolve }) {
  return (
    <div
      data-testid="rebelteam-lightbox"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)",
        backdropFilter: "blur(10px)", zIndex: 120,
        display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: "600px", width: "100%" }}>
        <img src={resolve(photo.image_url)} alt={photo.author_name} style={{ width: "100%", maxHeight: "75vh", objectFit: "contain", borderRadius: "8px" }} />
        <div style={{ color: "#FFF", padding: "1rem 0", textAlign: "center" }}>
          <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.9rem", textTransform: "uppercase" }}>@{photo.author_name}</p>
          {photo.event_location && (
            <p style={{ color: "#FF005A", fontSize: "0.8rem", marginTop: "0.3rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.3rem" }}>
              <MapPin size={12} /> {photo.event_location}
            </p>
          )}
          {photo.caption && <p style={{ color: "#A1A1AA", fontSize: "0.9rem", marginTop: "0.6rem", fontStyle: "italic" }}>"{photo.caption}"</p>}
        </div>
      </div>
      <button onClick={onClose} data-testid="lightbox-close"
        style={{ position: "absolute", top: "20px", right: "20px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "50%", width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#FFF" }}>
        <X size={20} />
      </button>
    </div>
  );
}

function UploadModal({ onClose, onSuccess }) {
  const { t } = useLanguage();
  const [authorName, setAuthorName] = useState("");
  const [caption, setCaption] = useState("");
  const [location, setLocation] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { setError(t.rebelteam.tooLarge); return; }
    setError("");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!authorName.trim()) { setError(t.rebelteam.nameRequired); return; }
    if (!file) { setError(t.rebelteam.photoRequired); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const upRes = await axios.post(`${API}/fan-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await axios.post(`${API}/fan-photos`, {
        author_name: authorName.trim(),
        image_url: upRes.data.url,
        caption: caption.trim() || null,
        event_location: location.trim() || null,
      });
      setSuccess(true);
      setTimeout(() => { onSuccess(); onClose(); }, 2200);
    } catch (err) {
      setError(err.response?.data?.detail || "Nahrávání selhalo");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="rebelteam-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(8px)", zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
      }}
    >
      <div onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0A0A0A",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "16px",
          maxWidth: "480px", width: "100%",
          padding: "2rem", position: "relative",
          maxHeight: "90vh", overflow: "auto",
        }}
      >
        <button onClick={onClose} data-testid="modal-close"
          style={{ position: "absolute", top: "12px", right: "12px", background: "transparent", border: "none", cursor: "pointer", color: "#A1A1AA" }}>
          <X size={20} />
        </button>

        {success ? (
          <div style={{ textAlign: "center", padding: "2rem 0" }}>
            <div style={{ width: "60px", height: "60px", borderRadius: "50%", background: "rgba(0,229,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem" }}>
              <Sparkles size={28} color="#00E5FF" />
            </div>
            <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "1rem", color: "#FFFFFF", fontWeight: 800, marginBottom: "0.5rem" }}>
              {t.rebelteam.thanks}
            </p>
            <p style={{ color: "#A1A1AA", fontSize: "0.8rem", marginTop: "0.4rem" }}>
              {t.rebelteam.pendingNote}
            </p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h3 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 900, fontSize: "1.2rem", color: "#FFFFFF", textTransform: "uppercase", marginBottom: "0.5rem" }}>
              {t.rebelteam.shareBtn}
            </h3>
            <p style={{ color: "#A1A1AA", fontSize: "0.75rem", marginBottom: "1.2rem", fontFamily: "Manrope, sans-serif" }}>
              {t.rebelteam.modalHint}
            </p>

            <div
              onClick={() => fileRef.current?.click()}
              data-testid="upload-dropzone"
              style={{
                border: "2px dashed rgba(255,0,90,0.3)",
                borderRadius: "12px",
                padding: "2rem 1rem",
                textAlign: "center",
                cursor: "pointer",
                marginBottom: "1rem",
                background: preview ? "transparent" : "rgba(255,0,90,0.03)",
                minHeight: "180px",
                display: "flex", alignItems: "center", justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {preview ? (
                <img src={preview} alt="preview" style={{ width: "100%", maxHeight: "240px", objectFit: "contain" }} />
              ) : (
                <div>
                  <Upload size={32} color="#FF005A" style={{ margin: "0 auto 0.7rem" }} />
                  <p style={{ color: "#FFFFFF", fontFamily: "Unbounded, sans-serif", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                    {t.rebelteam.uploadPhoto}
                  </p>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display: "none" }} data-testid="fan-file-input" />
            </div>

            <input data-testid="fan-name-input" type="text" placeholder={t.rebelteam.yourName} value={authorName} onChange={(e) => setAuthorName(e.target.value)}
              style={{ background: "#050505", border: "1px solid rgba(255,255,255,0.1)", color: "#FFFFFF", padding: "0.7rem", width: "100%", marginBottom: "0.7rem", fontFamily: "Manrope, sans-serif", borderRadius: "6px", outline: "none" }} required />
            <input data-testid="fan-location-input" type="text" placeholder={t.rebelteam.concertLocation} value={location} onChange={(e) => setLocation(e.target.value)}
              style={{ background: "#050505", border: "1px solid rgba(255,255,255,0.1)", color: "#FFFFFF", padding: "0.7rem", width: "100%", marginBottom: "0.7rem", fontFamily: "Manrope, sans-serif", borderRadius: "6px", outline: "none" }} />
            <textarea data-testid="fan-caption-input" rows={2} placeholder={t.rebelteam.caption} value={caption} onChange={(e) => setCaption(e.target.value)}
              style={{ background: "#050505", border: "1px solid rgba(255,255,255,0.1)", color: "#FFFFFF", padding: "0.7rem", width: "100%", marginBottom: "1rem", fontFamily: "Manrope, sans-serif", borderRadius: "6px", outline: "none", resize: "vertical" }} />

            {error && <p data-testid="upload-error" style={{ color: "#FF005A", fontSize: "0.8rem", marginBottom: "0.8rem" }}>{error}</p>}

            <div className="flex gap-2">
              <button type="button" data-testid="modal-cancel" onClick={onClose} className="btn-outline-rebel rounded-none" style={{ fontSize: "0.65rem", padding: "0.7rem 1.2rem", flex: 1 }}>
                {t.rebelteam.cancel}
              </button>
              <button type="submit" data-testid="fan-submit-btn" disabled={submitting} className="btn-rebel rounded-none" style={{ fontSize: "0.65rem", padding: "0.7rem 1.2rem", flex: 2 }}>
                {submitting ? t.rebelteam.uploading : t.rebelteam.submit}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
